// ─── Auth & Tenant ────────────────────────────────────────────────────────────

export interface User {
  id: string
  email: string
  full_name: string
  avatar_url?: string
}

export type UserRole = 'owner' | 'admin' | 'location_admin' | 'manager' | 'cashier' | 'accountant' | 'viewer' | 'super_admin'

export interface Tenant {
  id: string
  name: string
  slug: string
  currency: string
  country: string
  plan: 'starter' | 'growth' | 'pro'
  plan_expires_at: string | null
  erp_company: string
  erp_api_key: string | null
  erp_api_secret: string | null
  logo_url: string | null
  owner_id: string
  is_active: boolean
  settings: TenantSettings
  business_type: string | null
  onboarding_completed: boolean | null
  onboarding_step: number | null
  enforce_plan_limits?: boolean | null
  storefront_settings?: StorefrontSettings | null
}

// Palstro Commerce storefront config blob (tenants.storefront_settings, mig 278).
// Loosely typed — the DB blob is schemaless and holds more keys than L7.1 edits
// (money/branding/zones/slug set elsewhere). Only the L7.1-editable keys are named;
// the index signature keeps the rest (payment_account_id, hero_images, zones, …)
// readable without widening this shipment's surface.
export interface DeliveryZone {
  name: string
  fee: number
}

export interface StorefrontSettings {
  shop_slug?: string
  shop_name?: string
  tagline?: string
  whatsapp_phone?: string
  support_email?: string
  physical_address?: string
  store_state?: string
  store_city?: string
  commerce_enabled?: boolean
  pod_enabled?: boolean
  delivery_enabled?: boolean
  // Payments (mig 358) — explicit card on/off. Fail-open: absent => on for keyed shops.
  paystack_enabled?: boolean
  // BT.1 — "Pay by transfer" (direct bank transfer). Buyer-facing display fields
  // written via set_storefront_settings' whitelist (mig 354); never secret.
  bank_transfer_enabled?: boolean
  bank_name?: string
  bank_account_number?: string
  bank_account_name?: string
  zones?: DeliveryZone[]
  online_warehouse_id?: string
  logo_url?: string
  hero_image_url?: string
  hero_images?: string[]
  primary_color?: string
  [key: string]: unknown
}

// Online-store paid add-on subscription state (mig 332 tenant_online_store +
// platform_settings pricing). Read via get_online_store_status; drives the top-bar
// OnlineStorePill. `status` is server-computed from expires_at (authoritative).
export type OnlineStoreStatus = 'inactive' | 'trialing' | 'active' | 'expired'

export interface OnlineStoreState {
  status: OnlineStoreStatus
  expires_at: string | null
  started_at: string | null
  last_amount: number | null
  last_paid_at: string | null
  price_ngn: number
  period_months: number
  trial_days: number
  enabled: boolean
  eligible: boolean
}

export interface TenantSettings {
  tax_rate?: number
  vat_number?: string
  apply_vat_on_pos?: boolean
  apply_vat_on_invoices?: boolean
  withholding_tax_rate?: number
  default_warehouse?: string
  pos_require_customer?: boolean
  low_stock_threshold?: number
  fiscal_year_start?: string // MM-DD
  payroll_cycle?: 'weekly' | 'biweekly' | 'monthly'
  enable_multi_location?: boolean
  enable_barcode_scanner?: boolean
  // Receipt printout add-ons (render-only). Empty/absent = nothing extra prints.
  receipt_header_text?: string | null
  receipt_footer_text?: string | null
  receipt_show_balance?: boolean
  receipt_paper_width?: '58' | '80'
  receipt_printer_name?: string | null
  // Business identity shown on the invoice header (printed only when set).
  business_address?: string | null
  business_mobile?: string | null
}

// ─── Currency ────────────────────────────────────────────────────────────────

export const SUPPORTED_CURRENCIES = [
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GHS', symbol: 'GH₵', name: 'Ghanaian Cedi' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
] as const

export type CurrencyCode = (typeof SUPPORTED_CURRENCIES)[number]['code']

// ─── Common pagination ────────────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  pageSize: number
}

// ─── Items / Products ─────────────────────────────────────────────────────────

export interface Item {
  name: string
  item_code: string
  item_name: string
  description?: string
  item_group: string
  uom: string
  standard_rate: number
  valuation_rate?: number
  stock_qty?: number
  image?: string
  barcode?: string
  is_stock_item: boolean
  is_sales_item: boolean
  is_purchase_item: boolean
  disabled: boolean
  custom_tax_rate?: number
}

// ─── Sales ───────────────────────────────────────────────────────────────────

export interface SalesOrder {
  name: string
  customer: string
  customer_name: string
  transaction_date: string
  delivery_date: string
  status: SalesOrderStatus
  grand_total: number
  taxes_and_charges_added: number
  currency: string
  items: SalesOrderItem[]
}

export type SalesOrderStatus = 'Draft' | 'To Deliver and Bill' | 'To Bill' | 'To Deliver' | 'Completed' | 'Cancelled' | 'Closed'

export interface SalesOrderItem {
  item_code: string
  item_name: string
  qty: number
  rate: number
  amount: number
  uom: string
}

// ─── POS ─────────────────────────────────────────────────────────────────────

export interface POSCartItem {
  item_code: string
  item_name: string
  qty: number
  rate: number
  amount: number
  uom: string
  image?: string
  barcode?: string
}

export interface POSInvoice {
  customer: string
  pos_profile: string
  items: POSCartItem[]
  payments: POSPayment[]
  discount_amount?: number
  grand_total: number
  change_amount?: number
}

export interface POSPayment {
  mode_of_payment: string
  amount: number
}

// ─── Inventory / Stock ────────────────────────────────────────────────────────

export interface StockEntry {
  name: string
  stock_entry_type: string
  posting_date: string
  from_warehouse?: string
  to_warehouse?: string
  items: StockEntryItem[]
  total_amount?: number
}

export interface StockEntryItem {
  item_code: string
  item_name: string
  qty: number
  uom: string
  s_warehouse?: string
  t_warehouse?: string
  basic_rate?: number
}

export interface Warehouse {
  name: string
  warehouse_name: string
  parent_warehouse?: string
  company: string
  is_group: boolean
  disabled: boolean
}

// ─── Purchases ───────────────────────────────────────────────────────────────

export interface PurchaseOrder {
  name: string
  supplier: string
  supplier_name: string
  transaction_date: string
  schedule_date: string
  status: string
  grand_total: number
  currency: string
  items: PurchaseOrderItem[]
}

export interface PurchaseOrderItem {
  item_code: string
  item_name: string
  qty: number
  rate: number
  amount: number
  uom: string
  warehouse: string
}

// ─── Expenses ────────────────────────────────────────────────────────────────

export interface Expense {
  name: string
  employee?: string
  expense_approver?: string
  posting_date: string
  company: string
  status: 'Draft' | 'Pending' | 'Approved' | 'Rejected' | 'Paid'
  total_claimed_amount: number
  expenses: ExpenseItem[]
}

export interface ExpenseItem {
  expense_date: string
  expense_type: string
  description?: string
  amount: number
  sanctioned_amount?: number
  default_account?: string
}

// ─── Contacts ────────────────────────────────────────────────────────────────

export interface Contact {
  name: string
  full_name: string
  email_id?: string
  mobile_no?: string
  phone?: string
  company_name?: string
  address?: string
  customer_name?: string
  supplier_name?: string
  lead_name?: string
}

export interface Customer {
  name: string
  customer_name: string
  customer_type: 'Individual' | 'Company'
  customer_group: string
  territory: string
  mobile_no?: string
  email_id?: string
  default_currency?: string
  credit_limit?: number
}

export interface Supplier {
  name: string
  supplier_name: string
  supplier_type: string
  supplier_group: string
  country?: string
  mobile_no?: string
  email_id?: string
  default_currency?: string
}

// ─── Payroll ─────────────────────────────────────────────────────────────────

export interface Employee {
  name: string
  employee_name: string
  department?: string
  designation?: string
  date_of_joining: string
  status: 'Active' | 'Left' | 'On Leave'
  salary_mode?: string
  bank_ac_no?: string
  bank_name?: string
  cell_number?: string
  company_email?: string
  image?: string
}

export interface SalarySlip {
  name: string
  employee: string
  employee_name: string
  start_date: string
  end_date: string
  posting_date: string
  gross_pay: number
  net_pay: number
  total_deduction: number
  status: string
}

// ─── Accounting ───────────────────────────────────────────────────────────────

export interface JournalEntry {
  name: string
  voucher_type: string
  posting_date: string
  user_remark?: string
  total_debit: number
  total_credit: number
  docstatus: number
  accounts: JournalEntryAccount[]
}

export interface JournalEntryAccount {
  account: string
  debit_in_account_currency: number
  credit_in_account_currency: number
  party_type?: string
  party?: string
}

// ─── Budget ───────────────────────────────────────────────────────────────────

export interface Budget {
  name: string
  cost_center?: string
  fiscal_year: string
  monthly_distribution?: string
  budget_against: string
  accounts: BudgetAccount[]
}

export interface BudgetAccount {
  account: string
  budget_amount: number
}

// ─── Notifications ────────────────────────────────────────────────────────────

export interface Notification {
  id: string
  created_at: string
  title: string
  body: string
  type: 'info' | 'warning' | 'error' | 'success'
  is_read: boolean
  link?: string | null
}

// ─── Audit ────────────────────────────────────────────────────────────────────

export interface AuditLog {
  id: string
  created_at: string
  user_id: string
  action: string
  resource_type: string
  resource_id?: string | null
  before_data?: unknown
  after_data?: unknown
  ip_address?: string | null
}

// ─── Reports ─────────────────────────────────────────────────────────────────

export interface ReportFilter {
  from_date: string
  to_date: string
  company?: string
  cost_center?: string
  warehouse?: string
}
